import { Controller } from '../utils/controller';

export default class extends Controller {
  static targets = [
    'carouselIndicator',
    'carouselIndicatorContainer',
    'carouselImageContainer',
    'carouselPrevButton',
    'carouselNextButton',
    'carouselThumbnails',
    'carouselThumbnailContainer',
    'carouselThumbnailPrevButton',
    'carouselThumbnailNextButton'
  ];
  static requiredTargets = [];

  static classes = [
    'buttonDisabled',
    'buttonEnabled',
    'imageSlideTransition',
    'paginationActive',
    'paginationInactive',
    'thumbnailActive',
    'thumbnailInactive'
  ];
  static requiredClasses = [];

  static values = {
    imageCount: Number,
    skuId: String,
    imageSlideThresholdPercentage: {
      type: Number,
      default: 25
    },
    thumbnailMoveAmount: {
      type: Number,
      default: 3
    },
    thumbnailDirection: {
      type: String,
      default: 'Y'
    }
  };

  // TARGETS
  declare carouselImageContainerTarget: HTMLElement;
  declare carouselIndicatorContainerTarget: HTMLElement;
  declare carouselNextButtonTarget: HTMLElement;
  declare carouselPrevButtonTarget: HTMLElement;
  declare carouselThumbnailContainerTarget: HTMLElement;
  declare carouselThumbnailNextButtonTarget: HTMLElement;
  declare carouselThumbnailPrevButtonTarget: HTMLElement;
  declare carouselThumbnailsTarget: HTMLElement;
  declare readonly hasCarouselImageContainerTarget: boolean;
  declare readonly hasCarouselIndicatorContainerTarget: boolean;
  declare readonly hasCarouselNextButtonTarget: boolean;
  declare readonly hasCarouselPrevButtonTarget: boolean;
  declare readonly hasCarouselThumbnailNextButtonTarget: boolean;
  declare readonly hasCarouselThumbnailPrevButtonTarget: boolean;

  // VALUES
  declare imageSlideThresholdPercentageValue: number;

  // CLASSES
  declare buttonDisabledClasses: string[];
  declare buttonEnabledClasses: string[];
  declare imageSlideTransitionClasses: string[];
  declare paginationActiveClasses: string[];
  declare paginationInactiveClasses: string[];
  declare thumbnailActiveClasses: string[];
  declare thumbnailInactiveClasses: string[];
  declare thumbnailMoveAmountValue: number;
  declare thumbnailDirectionValue: string;

  // LOCAL STATE
  private _activeSlideIndex: number;
  private _activeThumbnailIndex: number;
  private numberOfSlides: number;
  private dragPercentageDistance: number;
  private dragStarted: boolean;
  private dragStartedDistance: number;

  initialize(): void {
    this._activeSlideIndex = 0;
    this._activeThumbnailIndex = 0;
    this.numberOfSlides = 0;
    this.dragPercentageDistance = 0;
    this.dragStarted = false;
    this.dragStartedDistance = 0;
  }

  connect() {
    document.addEventListener('sizeSelected', this.resetActive);

    this.setSlider();
  }

  disconnect() {
    document.removeEventListener('sizeSelected', this.resetActive);
  }

  resetActive = () => {
    this.activeSlideIndex = 0;
    this.activeThumbnailIndex = 0;
    this.setSlider();
  };

  setSlider() {
    if (!this.hasCarouselImageContainerTarget) {
      return;
    }
    this.numberOfSlides = this.carouselImageContainerTarget.children.length;
    this.updateActiveState();
    if (this.hasCarouselThumbnailPrevButtonTarget) {
      this.updateThumbnailNavigationDisplay();
    }
  }

  swipe(e) {
    if (!this.dragStarted) {
      return false;
    }
    const maxWidth = this.carouselImageContainerTarget.clientWidth;
    const isGoingBackwards = e.clientX > this.dragStartedDistance;
    const distance = Math.abs(this.dragStartedDistance - e.clientX);
    const dragAmount = (isGoingBackwards ? -1 : 1) * (distance / maxWidth);
    const newSlidePosition = this.activeSlideIndex + dragAmount;
    this.dragPercentageDistance =
      (isGoingBackwards ? 1 : -1) * Math.min(100, (100 * distance) / maxWidth);
    this.slideToSlidePosition(newSlidePosition);
  }

  toggleDrag(e) {
    e.preventDefault();
    if (!this.dragStarted && e.type === 'mousedown') {
      this.dragStarted = true;
      this.dragStartedDistance = e.screenX;
      this.carouselImageContainerTarget.classList.remove(...this.imageSlideTransitionClasses);
    }
    if (this.dragStarted && e.type === 'mouseup') {
      this.dragStarted = false;
      this.dragStartedDistance = 0;
      if (Math.abs(this.dragPercentageDistance) >= this.imageSlideThresholdPercentageValue) {
        // If translateX is positive, it means we are swiping towards the right which means go back an image
        this.activeSlideIndex =
          this.dragPercentageDistance > 0 ? this.activeSlideIndex - 1 : this.activeSlideIndex + 1;
      }
      this.updateActiveState();
    }
  }

  analyticsAltImageTrigger(altIndex) {
    const altImageEvent = new CustomEvent('altImage.clicked', {
      detail: {
        altImageAction: 'swipe',
        altImagePosition: altIndex
      }
    });
    window.dispatchEvent(altImageEvent);
  }

  applyImageContainerTranslateProperty(percentage) {
    if (this.hasCarouselImageContainerTarget) {
      this.carouselImageContainerTarget.style.transform = `translateX(${percentage}%)`;
    }
  }

  get activeSlideIndex() {
    return this._activeSlideIndex;
  }

  set activeSlideIndex(index) {
    const newIndex = Number(index);
    this.analyticsAltImageTrigger(newIndex + 1);
    const max = this.numberOfSlides - 1;
    const min = 0;
    const newValue = (() => {
      if (newIndex > max) {
        return max;
      }
      if (newIndex < min) {
        return min;
      }
      return newIndex;
    })();
    this._activeSlideIndex = newValue;
  }

  setActiveSlideViaPagination(e) {
    this.activeSlideIndex = e.target.dataset.index;
    this.updateActiveState();
  }

  slideToSlidePosition(position) {
    this.applyImageContainerTranslateProperty(position * -100);
  }

  // button navigation
  next() {
    this.activeSlideIndex++;
    this.updateActiveState();
  }

  prev() {
    this.activeSlideIndex--;
    this.updateActiveState();
  }

  // thumbnail navigation
  get activeThumbnailIndex() {
    return this._activeThumbnailIndex;
  }

  set activeThumbnailIndex(index) {
    const newIndex = Number(index);
    const max = Math.ceil((this.numberOfSlides - 1) / this.thumbnailMoveAmountValue);
    const min = 0;
    const newValue = (() => {
      if (newIndex > max) {
        return max;
      }
      if (newIndex < min) {
        return min;
      }
      return newIndex;
    })();
    this._activeThumbnailIndex = newValue;
  }

  setActiveThumbnail(e) {
    this.activeThumbnailIndex = e.target.dataset.index / this.thumbnailMoveAmountValue;
    this.setThumbnailMovement();
  }

  setThumbnailMovement() {
    const thumbnailMovement =
      this.activeThumbnailIndex * (this.thumbnailMoveAmountValue / this.numberOfSlides);
    this.slideToThumbnailPosition(thumbnailMovement);
  }

  nextThumbnail() {
    this.activeThumbnailIndex++;
    this.setThumbnailMovement();
  }

  prevThumbnail() {
    this.activeThumbnailIndex--;
    this.setThumbnailMovement();
  }

  slideToThumbnailPosition(position) {
    this.applyThumbnailContainerTranslateProperty(position * -100);
    this.updateThumbnailNavigationDisplay();
  }

  applyThumbnailContainerTranslateProperty(percentage) {
    this.carouselThumbnailContainerTarget.style.transform = `translate${this.thumbnailDirectionValue}(${percentage}%)`;
  }

  updatePaginationDisplay() {
    if (this.hasCarouselImageContainerTarget) {
      Array.from(this.carouselImageContainerTarget.children).forEach(
        (element: HTMLElement, index) => {
          const isActive = index === this.activeSlideIndex;
          element.ariaSelected = String(isActive);
          if (isActive) {
            element.focus();
          }
        }
      );
    }

    if (this.hasCarouselIndicatorContainerTarget) {
      Array.from(this.carouselIndicatorContainerTarget.children).forEach(
        (element: HTMLElement, index) => {
          if (index === this.activeSlideIndex) {
            element.classList.remove(...this.paginationInactiveClasses);
            element.classList.add(...this.paginationActiveClasses);
          } else {
            element.classList.remove(...this.paginationActiveClasses);
            element.classList.add(...this.paginationInactiveClasses);
          }
        }
      );
    }

    if (this.hasCarouselThumbnailPrevButtonTarget) {
      Array.from(this.carouselThumbnailContainerTarget.children).forEach(
        (element: HTMLElement, index) => {
          if (index === this.activeSlideIndex) {
            element.classList.remove(...this.thumbnailInactiveClasses);
            element.classList.add(...this.thumbnailActiveClasses);
          } else {
            element.classList.remove(...this.thumbnailActiveClasses);
            element.classList.add(...this.thumbnailInactiveClasses);
          }
        }
      );
    }
  }

  // arrows buttons on top of images in image carousel
  updateImageNavigationDisplay() {
    if (this.hasCarouselPrevButtonTarget && this.activeSlideIndex == 0) {
      this.carouselPrevButtonTarget.classList.remove(...this.buttonEnabledClasses);
      this.carouselPrevButtonTarget.classList.add(...this.buttonDisabledClasses);
    } else {
      this.carouselPrevButtonTarget.classList.remove(...this.buttonDisabledClasses);
      this.carouselPrevButtonTarget.classList.add(...this.buttonEnabledClasses);
    }

    if (this.hasCarouselNextButtonTarget && this.activeSlideIndex == this.numberOfSlides - 1) {
      this.carouselNextButtonTarget.classList.remove(...this.buttonEnabledClasses);
      this.carouselNextButtonTarget.classList.add(...this.buttonDisabledClasses);
    } else {
      this.carouselNextButtonTarget.classList.remove(...this.buttonDisabledClasses);
      this.carouselNextButtonTarget.classList.add(...this.buttonEnabledClasses);
    }
  }

  // arrows buttons on top of thumbnails in thumbnail carousel
  updateThumbnailNavigationDisplay() {
    if (this.hasCarouselThumbnailPrevButtonTarget && this.activeThumbnailIndex == 0) {
      this.carouselThumbnailPrevButtonTarget.classList.remove(...this.buttonEnabledClasses);
      this.carouselThumbnailPrevButtonTarget.classList.add(...this.buttonDisabledClasses);
    } else {
      this.carouselThumbnailPrevButtonTarget.classList.remove(...this.buttonDisabledClasses);
      this.carouselThumbnailPrevButtonTarget.classList.add(...this.buttonEnabledClasses);
    }
    if (
      this.hasCarouselThumbnailNextButtonTarget &&
      this.activeThumbnailIndex >= Math.floor(this.numberOfSlides / this.thumbnailMoveAmountValue)
    ) {
      this.carouselThumbnailNextButtonTarget.classList.remove(...this.buttonEnabledClasses);
      this.carouselThumbnailNextButtonTarget.classList.add(...this.buttonDisabledClasses);
    } else {
      this.carouselThumbnailNextButtonTarget.classList.remove(...this.buttonDisabledClasses);
      this.carouselThumbnailNextButtonTarget.classList.add(...this.buttonEnabledClasses);
    }
  }

  updateActiveState() {
    if (this.hasCarouselImageContainerTarget) {
      this.carouselImageContainerTarget.classList.add(...this.imageSlideTransitionClasses);
    }
    this.slideToSlidePosition(this.activeSlideIndex);
    this.updatePaginationDisplay();
    if (this.hasCarouselPrevButtonTarget) {
      this.updateImageNavigationDisplay();
    }
    if (this.hasCarouselThumbnailNextButtonTarget) {
      this.activeThumbnailIndex = this.activeSlideIndex / this.thumbnailMoveAmountValue;
      this.setThumbnailMovement();
    }
  }

  skuIdValueChanged(value, previousValue) {
    if (previousValue != '') {
      this.initialize();
      this.setSlider();
    }
  }
}
